import {
  ActionIcon,
  Box,
  Checkbox,
  Group,
  LoadingOverlay,
  Menu,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import { IconDotsVertical, IconAlertTriangle } from "@tabler/icons-react";
import CustomTable from "../../components/tables/CustomTable";
import TablePagination from "../../components/pagination";
import { MeterCutoffsDataType } from "../../types";
import { formatDate } from "../../utils/date";
import { parseErrorCode, getErrorCodeSummary } from "../../utils/errorCodes";
import { formatNumberWithCommas } from "../../utils/numbers";

type DailyCutoffsTableProps = MeterCutoffsDataType["dailyCutoffs"] & {
  onPageChange: (page: number) => void;
  onLimitSelect: (limit: string | null) => void;
  onDailyCutoffRowClick: (id: number) => void;
  dailyCutoffRecordId: string | null;
  showLiters: boolean;
};

export default function DailyCutoffsTable({
  loading,
  results,
  error,
  limit,
  page,
  totalPages,
  onLimitSelect,
  onPageChange,
  onDailyCutoffRowClick,
  dailyCutoffRecordId,
  showLiters,
}: DailyCutoffsTableProps) {
  const headers = [
    { label: "", accessor: "checkbox" },
    { label: "Date & Time", accessor: "dateAndTime" },
    { label: showLiters ? "Previous Rd. (l)" : "Previous Rd. (units)", accessor: "previousVolume" },
    { label: showLiters ? "Current Rd. (l)" : "Current Rd. (units)", accessor: "totalVolume" },
    { label: showLiters ? "Consumption (l)" : "Consumption (units)", accessor: "consumption" },
    { label: "Temp (°C)", accessor: "temp" },
    { label: "Alarms", accessor: "alarms" },
    { label: "", accessor: "actions" },
  ];

  // Light red shade for alarm telegrams
  const ALARM_ROW_STYLE = {
    backgroundColor: "#ffe6e6",
    hoverBackgroundColor: "#ffcccc",
  };

  const data = results?.map((result) => {
    const errors = parseErrorCode(result?.errorCode || 0);

    return {
      id: result?.id,
      _rowStyle: result?.isAlarmTelegram ? ALARM_ROW_STYLE : undefined,
      checkbox: {
        component: (
          <Checkbox
            readOnly
            checked={
              String(dailyCutoffRecordId) === String(result?.id) ? true : false
            }
          />
        ),
      },
      dateAndTime: <Text size="sm">{formatDate(result?.deviceDateTime)}</Text>,
      previousVolume: (() => {
        const volume = result?.previousVolume;
        if (volume === null || volume === undefined) return <Text size="sm" c="dimmed">--</Text>;
        if (showLiters) return <Text size="sm">{formatNumberWithCommas(volume)}</Text>;
        const truncated = Math.trunc(volume / 10) / 100;
        const [whole, dec] = truncated.toFixed(2).split(".");
        return (
          <Group gap={0}>
            <Text size="sm" fw={700}>{formatNumberWithCommas(Number(whole))}</Text>
            <Text size="xs" fw={400} c="dimmed">.{dec}</Text>
          </Group>
        );
      })(),
      totalVolume: (() => {
        const volume = result?.totalVolume;
        if (!(volume >= 0)) return <Text size="sm">--</Text>;
        if (showLiters) return <Text size="sm">{formatNumberWithCommas(volume)}</Text>;
        const truncated = Math.trunc(volume / 10) / 100;
        const [whole, dec] = truncated.toFixed(2).split(".");
        return (
          <Group gap={0}>
            <Text size="sm" fw={700}>{formatNumberWithCommas(Number(whole))}</Text>
            <Text size="xs" fw={400} c="dimmed">.{dec}</Text>
          </Group>
        );
      })(),
      consumption: (() => {
        const current = result?.totalVolume;
        const previous = result?.previousVolume;
        if (previous === null || previous === undefined) return <Text size="sm" c="dimmed">--</Text>;
        const diff = current - previous;
        const isNegative = diff < 0;
        if (showLiters) return <Text size="sm" fw={600} c={isNegative ? "red" : undefined}>{formatNumberWithCommas(diff)}</Text>;
        const truncated = Math.trunc(Math.abs(diff) / 10) / 100;
        const [whole, dec] = truncated.toFixed(2).split(".");
        return (
          <Group gap={0}>
            <Text size="sm" fw={700} c={isNegative ? "red" : undefined}>{isNegative ? "-" : ""}{formatNumberWithCommas(Number(whole))}</Text>
            <Text size="xs" fw={400} c={isNegative ? "red" : "dimmed"}>.{dec}</Text>
          </Group>
        );
      })(),
      temp: <Text size="sm">{result?.temperature}</Text>,
      alarms: (() => {
        const summary = getErrorCodeSummary(result?.errorCode || 0);
        const hasAlarm = summary !== "No Errors";
        return (
          <Text size="sm" c={hasAlarm ? "red" : "dimmed"}>
            {hasAlarm ? summary : "None"}
          </Text>
        );
      })(),
      actions: result?.isAlarmTelegram
        ? {
            component: (
              <Menu shadow="md" width={280} position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconDotsVertical size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>
                    <Text size="xs" c="dimmed" fw={600}>
                      Alarm Details
                    </Text>
                  </Menu.Label>
                  {errors.map((error, index) => (
                    <Menu.Item
                      key={index}
                      leftSection={<IconAlertTriangle size={16} color="red" />}
                      closeMenuOnClick={false}
                    >
                      <Stack gap={2}>
                        <Text size="sm" fw={600}>
                          {error.label}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {error.description}
                        </Text>
                      </Stack>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            ),
            hoverableCell: false,
            onCellClick: () => {
              // Prevent row click when clicking on the menu
            },
          }
        : null,
    };
  });
  
  return (
    <Box pos={"relative"} mih={"20vh"}>
      <LoadingOverlay
        visible={loading}
        zIndex={1000}
        overlayProps={{
          radius: "sm",
          blur: 0.5,
          backgroundOpacity: 0.5,
          color: "#edf2fb",
        }}
      />
      {error ? (
        <Text my={"md"} fw={600} size="lg" ta={"center"}>
          Failed to load data. <br /> Please contact admin.
        </Text>
      ) : data?.length ? (
        <>
          <CustomTable
            headers={headers}
            rows={data}
            hoverableRow={true}
            onRowClick={(rowData) => {
              onDailyCutoffRowClick(rowData?.id);
            }}
          />
          <Space h="sm" />
          <TablePagination
            currentPage={page}
            limit={limit}
            totalPages={totalPages}
            onLimitSelect={onLimitSelect}
            onPageChange={onPageChange}
          />
        </>
      ) : loading ? null : (
        <Text my={"md"} fw={600} size="lg" ta={"center"}>
          No data available.
        </Text>
      )}
    </Box>
  );
}
